import { Skeleton } from '../ui/skeleton';

/**
 * Reusable page skeleton loading component
 * Provides consistent loading states across the app
 */

// Default skeleton for most pages
export const PageSkeleton = ({ variant = 'default' }) => {
  const variants = {
    default: <DefaultSkeleton />,
    dashboard: <DashboardSkeleton />,
    list: <ListSkeleton />,
    form: <FormSkeleton />,
    admin: <AdminSkeleton />,
    adminTable: <AdminTableSkeleton />,
    profile: <ProfileSkeleton />,
  };

  return (
    <div className="min-h-screen bg-cream p-4" data-testid="page-skeleton">
      <div className="max-w-lg mx-auto space-y-4">
        {variants[variant] || variants.default}
      </div>
    </div>
  );
};

// Default skeleton
const DefaultSkeleton = () => (
  <>
    <Skeleton className="h-10 w-32" />
    <Skeleton className="h-28" />
    <Skeleton className="h-20" />
    <Skeleton className="h-16" />
  </>
);

// Dashboard page skeleton
const DashboardSkeleton = () => (
  <>
    <Skeleton className="h-10 w-32" />
    <Skeleton className="h-28 rounded-xl" />
    <Skeleton className="h-20 rounded-xl" />
    <div className="grid grid-cols-2 gap-3">
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
    <Skeleton className="h-16 rounded-xl" />
  </>
);

// List/History page skeleton
const ListSkeleton = () => (
  <>
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-8 w-48" />
    </div>
    <Skeleton className="h-20 rounded-xl mb-4" />
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-20 rounded-xl" />
      ))}
    </div>
  </>
);

// Form page skeleton (KYC, Bank Details, etc.)
const FormSkeleton = () => (
  <>
    <div className="flex items-center gap-3 mb-6">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-8 w-48" />
    </div>
    <div className="space-y-4">
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-12 rounded-lg" />
      <Skeleton className="h-24 rounded-lg" />
    </div>
    <Skeleton className="h-12 w-full rounded-lg mt-6" />
  </>
);

// Admin dashboard skeleton
const AdminSkeleton = () => (
  <>
    <Skeleton className="h-12 w-full rounded-lg" />
    <div className="grid grid-cols-2 gap-4">
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
    <Skeleton className="h-48 rounded-xl" />
  </>
);

// Admin table skeleton
const AdminTableSkeleton = () => (
  <>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-8 w-48" />
      </div>
      <Skeleton className="h-10 w-32 rounded-lg" />
    </div>
    <Skeleton className="h-12 rounded-lg mb-4" />
    <div className="space-y-2">
      {[...Array(8)].map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-lg" />
      ))}
    </div>
  </>
);

// Profile page skeleton
const ProfileSkeleton = () => (
  <>
    <div className="flex items-center gap-3 mb-6">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-8 w-32" />
    </div>
    <div className="flex flex-col items-center mb-6">
      <Skeleton className="h-24 w-24 rounded-full" />
      <Skeleton className="h-6 w-32 mt-3" />
      <Skeleton className="h-4 w-24 mt-1" />
    </div>
    <div className="space-y-3">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-xl" />
      ))}
    </div>
  </>
);

export default PageSkeleton;
